import {
  extractOriginalIndexFromId,
  calculateInsertionPosition,
} from "../utils/pdfTableViewerUtils";
import { useCallback } from "react";

/**
 * Extract page information for a specific row from selectedTable
 */
export const extractPageInfoForRow = (selectedTable, rowIndex) => {
  if (!selectedTable) return null;

  const pages = new Set();

  // Check all cells in this row to find which pages they belong to
  Object.keys(selectedTable).forEach((cellKey) => {
    if (cellKey.match(/^\d+_\d+$/)) {
      const [cellRowStr] = cellKey.split("_");
      const cellRowIndex = parseInt(cellRowStr);

      // If this cell belongs to our row, extract page info
      if (cellRowIndex === rowIndex) {
        const cellLocationData = selectedTable[cellKey];

        if (cellLocationData && Array.isArray(cellLocationData)) {
          const firstCellInfo = cellLocationData[0];
          if (firstCellInfo && firstCellInfo.page) {
            pages.add(firstCellInfo.page);
          }
        }
      }
    }
  });

  const result = Array.from(pages).sort((a, b) => a - b);

  return result;
};

/**
 * Hook for table operations - provides interface to row/column manipulation
 * Uses existing extracted table manipulation functions
 */
const useTableOperations = (tableState, idSystemState) => {
  const { tableData, setTableData, selectedTable } = tableState;
  const {
    rowIds,
    setRowIds,
    columnIds,
    setColumnIds,
    deletedRows,
    setDeletedRows,
    deletedColumns,
    setDeletedColumns,
  } = idSystemState;

  // Row deletion function - direct implementation using useCallback
  const removeRow = useCallback(
    (currentRowIndex, currentPage = 1) => {
      if (
        !tableData ||
        !rowIds ||
        currentRowIndex < 0 ||
        currentRowIndex >= tableData.rows.length
      ) {
        return;
      }

      // Find the ID of the row at the current position
      const rowIdToDelete = rowIds[currentRowIndex];
      if (!rowIdToDelete) {
        return;
      }

      // Extract the original position directly from the row ID
      const originalIndex = extractOriginalIndexFromId(rowIdToDelete);

      // Store the deleted row information with page data
      // Note: extractPageInfoForRow expects 1-based row index (to match selectedTable cell keys)
      // extractOriginalIndexFromId returns 0-based index, so we always add 1 for data rows
      let pageInfo = extractPageInfoForRow(selectedTable, originalIndex + 1);

      // Fallback: if no page info found (row might be already deleted),
      // assume it belongs to current page since user is deleting from current view
      if (!pageInfo || pageInfo.length === 0) {
        // Use the current page as fallback since user is deleting from current view
        pageInfo = [currentPage];
      }

      const deletedRowInfo = {
        id: rowIdToDelete,
        originalIndex: originalIndex,
        content: tableData.rows[currentRowIndex],
        pageInfo: pageInfo,
      };

      // Add to deleted rows map
      setDeletedRows((prev) => {
        const newMap = new Map(prev);
        newMap.set(rowIdToDelete, deletedRowInfo);
        return newMap;
      });

      // Remove from current table data
      const updatedData = {
        ...tableData,
        rows: tableData.rows.filter((_, index) => index !== currentRowIndex),
      };
      setTableData(updatedData);

      // Remove from current row IDs array
      setRowIds((prev) => prev.filter((_, index) => index !== currentRowIndex));
    },
    [tableData, rowIds, selectedTable, setTableData, setRowIds, setDeletedRows]
  );

  // Column deletion function - direct implementation using useCallback
  const removeColumnGlobally = useCallback(
    (currentColIndex) => {
      if (
        !tableData ||
        !columnIds ||
        currentColIndex < 0 ||
        currentColIndex >= tableData.headers.length
      ) {
        return;
      }

      // Find the ID of the column at the current position
      const columnIdToDelete = columnIds[currentColIndex];
      if (!columnIdToDelete) {
        return;
      }

      // Extract the original position directly from the column ID
      const originalIndex = extractOriginalIndexFromId(columnIdToDelete);

      // Extract column data for storage
      const columnContent = tableData.rows.map(
        (row) => row[currentColIndex] || ""
      );

      // Store the deleted column information
      const deletedColumnInfo = {
        id: columnIdToDelete,
        originalIndex: originalIndex,
        header: tableData.headers[currentColIndex],
        content: columnContent,
      };

      // Add to deleted columns map
      setDeletedColumns((prev) => {
        const newMap = new Map(prev);
        newMap.set(columnIdToDelete, deletedColumnInfo);
        return newMap;
      });

      // Remove from current table data
      const updatedHeaders = tableData.headers.filter(
        (_, index) => index !== currentColIndex
      );
      const updatedRows = tableData.rows.map((row) =>
        row.filter((_, index) => index !== currentColIndex)
      );

      const updatedData = {
        headers: updatedHeaders,
        rows: updatedRows,
      };
      setTableData(updatedData);

      // Remove from current column IDs array
      setColumnIds((prev) =>
        prev.filter((_, index) => index !== currentColIndex)
      );
    },
    [tableData, columnIds, setTableData, setColumnIds, setDeletedColumns]
  );

  // Recovery functions
  const recoverRowById = useCallback(
    (rowId) => {
      if (!tableData || !deletedRows.has(rowId)) return;

      const rowInfo = deletedRows.get(rowId);
      const { originalIndex, content } = rowInfo;

      // Calculate insertion position based on current table structure
      const insertPosition = Math.min(
        calculateInsertionPosition(originalIndex, deletedRows),
        tableData.rows.length
      );

      // Insert the row back
      const updatedRows = [...tableData.rows];
      updatedRows.splice(insertPosition, 0, content);

      const updatedData = {
        ...tableData,
        rows: updatedRows,
      };
      setTableData(updatedData);

      // Add the row ID back to current row IDs at the correct position
      const updatedRowIds = [...rowIds];
      updatedRowIds.splice(insertPosition, 0, rowId);
      setRowIds(updatedRowIds);

      // Remove from deleted tracking
      setDeletedRows((prev) => {
        const newMap = new Map(prev);
        newMap.delete(rowId);
        return newMap;
      });
    },
    [tableData, deletedRows, rowIds, setTableData, setRowIds, setDeletedRows]
  );

  const recoverColumnById = useCallback(
    (columnId) => {
      if (!tableData || !deletedColumns.has(columnId)) return;

      const columnInfo = deletedColumns.get(columnId);
      const { originalIndex, header, content } = columnInfo;

      // Calculate insertion position based on current table structure
      const insertPosition = Math.min(
        calculateInsertionPosition(originalIndex, deletedColumns),
        tableData.headers.length
      );

      // Insert the column back
      const updatedHeaders = [...tableData.headers];
      updatedHeaders.splice(insertPosition, 0, header);

      const updatedRows = tableData.rows.map((row, rowIndex) => {
        const newRow = [...row];
        const cellContent = content[rowIndex] || "";
        newRow.splice(insertPosition, 0, cellContent);
        return newRow;
      });

      const updatedData = {
        headers: updatedHeaders,
        rows: updatedRows,
      };
      setTableData(updatedData);

      // Add the column ID back to current column IDs at the correct position
      const updatedColumnIds = [...columnIds];
      updatedColumnIds.splice(insertPosition, 0, columnId);
      setColumnIds(updatedColumnIds);

      // Remove from deleted tracking
      setDeletedColumns((prev) => {
        const newMap = new Map(prev);
        newMap.delete(columnId);
        return newMap;
      });
    },
    [
      tableData,
      deletedColumns,
      columnIds,
      setTableData,
      setColumnIds,
      setDeletedColumns,
    ]
  );

  // Helper functions - Bulk recovery with single state update
  const recoverAllRows = useCallback(() => {
    if (!deletedRows || deletedRows.size === 0 || !tableData) return;

    // Sort deleted rows by original index for proper recovery order
    const sortedDeletedRows = Array.from(deletedRows.entries()).sort(
      ([, rowInfoA], [, rowInfoB]) =>
        rowInfoA.originalIndex - rowInfoB.originalIndex
    );

    // Prepare all the data for bulk recovery
    let updatedRows = [...tableData.rows];
    let updatedRowIds = [...rowIds];

    // Process each deleted row in order
    sortedDeletedRows.forEach(([rowId, rowInfo]) => {
      const { originalIndex, content } = rowInfo;

      // Insert at original position - since we're processing in sorted order,
      // each insertion will naturally push subsequent items to the right
      const insertPosition = Math.max(
        0,
        Math.min(originalIndex, updatedRows.length)
      );

      // Insert the row content and ID
      updatedRows.splice(insertPosition, 0, content);
      updatedRowIds.splice(insertPosition, 0, rowId);
    });

    // Update all state in a single batch
    const updatedData = {
      ...tableData,
      rows: updatedRows,
    };

    setTableData(updatedData);
    setRowIds(updatedRowIds);
    setDeletedRows(new Map()); // Clear all deleted rows
  }, [tableData, deletedRows, rowIds, setTableData, setRowIds, setDeletedRows]);

  const recoverAllColumns = useCallback(() => {
    if (!deletedColumns || deletedColumns.size === 0 || !tableData) return;

    // Sort deleted columns by original index for proper recovery order
    const sortedDeletedColumns = Array.from(deletedColumns.entries()).sort(
      ([, columnInfoA], [, columnInfoB]) =>
        columnInfoA.originalIndex - columnInfoB.originalIndex
    );

    // Prepare all the data for bulk recovery
    let updatedHeaders = [...tableData.headers];
    let updatedColumnIds = [...columnIds];
    let updatedRows = [...tableData.rows];

    // Process each deleted column in order
    sortedDeletedColumns.forEach(([columnId, columnInfo]) => {
      const { originalIndex, header, content } = columnInfo;

      // Insert header at original position
      const headerInsertPosition = Math.max(
        0,
        Math.min(originalIndex, updatedHeaders.length)
      );
      updatedHeaders.splice(headerInsertPosition, 0, header);
      updatedColumnIds.splice(headerInsertPosition, 0, columnId);

      // Insert column data into each row
      updatedRows = updatedRows.map((row, rowIndex) => {
        const newRow = [...row];
        const cellContent = content[rowIndex] || "";
        newRow.splice(headerInsertPosition, 0, cellContent);
        return newRow;
      });
    });

    // Update all state in a single batch
    const updatedData = {
      ...tableData,
      headers: updatedHeaders,
      rows: updatedRows,
    };

    setTableData(updatedData);
    setColumnIds(updatedColumnIds);
    setDeletedColumns(new Map()); // Clear all deleted columns
  }, [
    tableData,
    deletedColumns,
    columnIds,
    setTableData,
    setColumnIds,
    setDeletedColumns,
  ]);

  const getDeletedRowsInfo = useCallback(() => deletedRows || new Map(), [
    deletedRows,
  ]);
  const getDeletedColumnsInfo = useCallback(() => deletedColumns || new Map(), [
    deletedColumns,
  ]);

  // Clear all deletions using bulk recovery
  const clearAllDeletions = useCallback(() => {
    if (recoverAllRows) recoverAllRows();
    if (recoverAllColumns) recoverAllColumns();
  }, [recoverAllRows, recoverAllColumns]);

  // Provide convenience methods
  const getOperationStats = () => ({
    deletedRowsCount: deletedRows?.size || 0,
    deletedColumnsCount: deletedColumns?.size || 0,
    totalRows: tableData?.rows?.length || 0,
    totalColumns: tableData?.headers?.length || 0,
  });

  const hasDeletedItems = () => {
    return (deletedRows?.size || 0) > 0 || (deletedColumns?.size || 0) > 0;
  };

  return {
    // Row operations
    removeRow,
    recoverRowById,
    recoverAllRows,

    // Column operations
    removeColumnGlobally,
    recoverColumnById,
    recoverAllColumns,

    // Bulk operations
    clearAllDeletions,

    // Information
    getOperationStats,
    hasDeletedItems,
    getDeletedRowsInfo,
    getDeletedColumnsInfo,

    // Direct access to collections for advanced use
    deletedRows,
    deletedColumns,
  };
};

export default useTableOperations;
